import React, { useState } from 'react';
import { FileText, Link2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

const PermitLinking = ({ worksheet }) => {
  const [selectedPermit, setSelectedPermit] = useState(null);

  const activePermits = [
    {
      id: 'WP-2025-001',
      type: 'Hot Work',
      location: 'Unit 100 - Area A',
      equipment: 'HE-101',
      status: 'active',
      linkedRisks: 3,
      startDate: '2025-12-04T08:00:00Z',
      endDate: '2025-12-04T17:00:00Z'
    },
    {
      id: 'WP-2025-002',
      type: 'Confined Space',
      location: 'Tank Farm',
      equipment: 'TK-304',
      status: 'active',
      linkedRisks: 5,
      startDate: '2025-12-04T09:00:00Z',
      endDate: '2025-12-04T15:00:00Z'
    }
  ];

  const identifiedRisks = [
    {
      id: 'RISK-001',
      hazard: 'High Temperature Exposure',
      equipment: 'HE-101',
      riskRating: 15,
      linkedToPermit: 'WP-2025-001',
      status: 'linked'
    },
    {
      id: 'RISK-002',
      hazard: 'Chemical Exposure',
      equipment: 'HE-101',
      riskRating: 16,
      linkedToPermit: null,
      status: 'unlinked'
    }
  ];

  if (!worksheet) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Select a worksheet to manage permit linking</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Work Permit Linking</h2>
        <p className="text-sm text-gray-600">
          Connect identified risks to active work permits for {worksheet?.title}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Permits */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Work Permits</h3>

          <div className="space-y-3">
            {activePermits?.map((permit) => (
              <button
                key={permit?.id}
                onClick={() => setSelectedPermit(permit)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  selectedPermit?.id === permit?.id
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-gray-900">{permit?.id}</span>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {permit?.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium text-gray-900">{permit?.type}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Equipment:</span>
                    <span className="font-medium text-gray-900">{permit?.equipment}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Linked Risks:</span>
                    <span className="font-semibold text-blue-600">{permit?.linkedRisks}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
                  {new Date(permit.startDate)?.toLocaleString()} - {new Date(permit.endDate)?.toLocaleTimeString()}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Identified Risks */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Identified Risks</h3>

          <div className="space-y-3">
            {identifiedRisks?.map((risk) => (
              <div
                key={risk?.id}
                className={`p-4 rounded-lg border-2 ${
                  risk?.status === 'linked' ?'border-green-200 bg-green-50' :'border-orange-200 bg-orange-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{risk?.id}</span>
                  <div className="flex items-center gap-2">
                    {risk?.status === 'linked' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                    )}
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                      Risk: {risk?.riskRating}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-700 mb-2">{risk?.hazard}</p>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Equipment: {risk?.equipment}</span>
                  {risk?.linkedToPermit ? (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      {risk?.linkedToPermit}
                    </span>
                  ) : (
                    <button className="text-blue-600 hover:text-blue-700 font-medium">
                      Link to Permit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Linked:</span>
                <span className="ml-2 font-semibold text-green-600">
                  {identifiedRisks?.filter(r => r?.status === 'linked')?.length}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Unlinked:</span>
                <span className="ml-2 font-semibold text-orange-600">
                  {identifiedRisks?.filter(r => r?.status === 'unlinked')?.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermitLinking;